/**
 * "Guided tours" in Users → Edit (Bill 2026-10-07 role walkthroughs): which
 * tours this person has finished or closed, and a reset so every tour starts
 * again the next time they open the Teams app or a panel. The reset is
 * recorded in Access history (ToursReset).
 */
import { useCallback, useEffect, useState } from 'react';
import type { TourProgressRow } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Spinner } from '@/components/primitives';

const TOUR_NAMES: Record<string, string> = {
  'rop-officer': 'Teams app - Officer: finding your way around',
  'rop-application': 'Teams app - Officer: opening an account',
  'rop-supervisor': 'Teams app - Supervisor and Manager',
  'rop-executive': 'Teams app - Executive',
  'admin-panel': 'Admin panel: people and settings',
  'admin-panel-content': 'Admin panel: your areas',
  'risk-panel': 'Risk Management panel',
  'sysops-panel': 'SysOps panel',
};

export function UserToursSection({ userId, onReset }: { userId: number; onReset?: () => void }): JSX.Element {
  const client = useApiClient();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ReadonlyArray<TourProgressRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null); setRows(null);
    try { setRows((await client.retail.tours.forUser(userId)).data); }
    catch (e) { setError((e as Error)?.message ?? 'Could not load'); }
  }, [client, userId]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  async function reset(): Promise<void> {
    setBusy(true); setError(null);
    try {
      const r = await client.retail.tours.resetForUser(userId);
      setNote(r.removed > 0 ? 'Reset. Their tours start again the next time they open the app or a panel.' : 'Nothing to reset - they have not taken a tour yet.');
      setConfirming(false);
      onReset?.();
      await load();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not reset');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="user-edit-testmode">
      <button type="button" className="link" onClick={() => setOpen((v) => !v)}>
        {open ? '▾' : '▸'} Guided tours
      </button>
      {open && (
        <div className="user-edit-testmode__body">
          {error && <Banner tone="err">{error}</Banner>}
          {note && <Banner tone="info">{note}</Banner>}
          {!rows && !error && <Spinner label="Loading tours…" />}
          {rows && rows.length === 0 && (
            <p className="cell-dim">No tours taken yet. The tour for their role starts by itself the first time they open the app (System → Settings → tours.enabled).</p>
          )}
          {rows && rows.length > 0 && (
            <table className="data-table">
              <thead><tr><th>Tour</th><th>Outcome</th><th>When</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.tourId}>
                    <td>{TOUR_NAMES[r.tourId] ?? r.tourId} <span className="cell-dim">v{r.version}</span></td>
                    <td>{r.status === 'completed' ? 'Finished' : `Closed at step ${r.lastStep + 1}`}</td>
                    <td>{new Date(r.updatedAt.endsWith('Z') ? r.updatedAt : `${r.updatedAt}Z`).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            {!confirming ? (
              <Button variant="ghost" size="sm" onClick={() => { setNote(null); setConfirming(true); }}>Reset tours…</Button>
            ) : (
              <>
                <span className="cell-dim">Every tour will start again for this person. Recorded in Access history.</span>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => void reset()} disabled={busy}>{busy ? 'Resetting…' : 'Reset tours'}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UserToursSection;
